import React, { useState, useEffect, useCallback } from 'react';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import dayjs from 'dayjs';
import { clone } from 'lodash';
import SelectableDateItem from './SelectableDateItem';
import SelectedDateItem from './SelectedDateItem';
import {
  CandidateScheduleWrapper,
  AvailableDatesSection,
  SelectedDatesSection,
} from './CandidateScheduleDateSelector.styles';

const CandidateScheduleDateSelector = ({
  selectedDates: routerSelectedDates,
  setSelectedDates: setRouterSelectedDates,
  selectedableDates,
  setSelectedableDates: setRouterSelectedableDates,
  singleDateChoice,
  isOrdinal,
  hasPreferenceOrderWithNoInterviewDate,
  onSyncReady, // New prop to expose sync function to parent
}) => {
  // Internal component state - independent of Router
  const [internalSelectedDates, setInternalSelectedDates] = useState([]);
  const [internalAvailableDates, setInternalAvailableDates] = useState([]);

  // Initialize internal state from Router data ONCE
  useEffect(() => {
    if (selectedableDates && selectedableDates.length > 0) {
      // Separate into selected and available based on API data
      const initialSelected = selectedableDates
        .filter((date) => date.isSelected === true)
        .sort((a, b) => (a.preferenceOrder || 0) - (b.preferenceOrder || 0))
        .map((date, index) => ({
          ...date,
          id: date.pk_InterviewDate || date.id,
          preference: index,
          preferenceOrder: index,
          formattedDate: dayjs(`${dayjs(date.DateOfInterview).format('MMM DD, YYYY')} ${date.StartTime}`).format(
            'MMM DD, YYYY hh:mm A',
          ),
        }));

      const initialAvailable = selectedableDates
        .filter((date) => !date.isSelected)
        .map((date) => ({
          ...date,
          id: date.pk_InterviewDate || date.id,
          formattedDate: dayjs(`${dayjs(date.DateOfInterview).format('MMM DD, YYYY')} ${date.StartTime}`).format(
            'MMM DD, YYYY hh:mm A',
          ),
        }));

      setInternalSelectedDates(initialSelected);
      setInternalAvailableDates(initialAvailable);
    }
  }, []); // Empty dependency - only run once on mount

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
  );

  // Handle clicking on available date to select it
  const handleSelectDate = useCallback(
    (dateId) => {
      const dateIndex = internalAvailableDates.findIndex(
        (date) => date.id === dateId || date.pk_InterviewDate === dateId,
      );
      if (dateIndex === -1) return;

      const dateToMove = internalAvailableDates[dateIndex];
      const updatedDate = {
        ...dateToMove,
        selected: true,
        isSelected: true,
        preference: internalSelectedDates.length,
        preferenceOrder: internalSelectedDates.length,
        id: dateToMove.pk_InterviewDate || dateToMove.id,
        formattedDate: dayjs(
          `${dayjs(dateToMove.DateOfInterview).format('MMM DD, YYYY')} ${dateToMove.StartTime}`,
        ).format('MMM DD, YYYY hh:mm A'),
      };

      setInternalSelectedDates([...internalSelectedDates, updatedDate]);
      setInternalAvailableDates(internalAvailableDates.filter((_, i) => i !== dateIndex));
    },
    [internalAvailableDates, internalSelectedDates],
  );

  // Handle clicking on selected date to remove it
  const handleRemoveDate = useCallback(
    (dateId) => {
      const dateIndex = internalSelectedDates.findIndex(
        (date) => date.id === dateId || date.pk_InterviewDate === dateId,
      );
      if (dateIndex === -1) return;

      const dateToMove = internalSelectedDates[dateIndex];
      const updatedDate = {
        ...dateToMove,
        selected: false,
        isSelected: false,
        preference: undefined,
        preferenceOrder: undefined,
      };

      const newSelectedDates = internalSelectedDates.filter((_, i) => i !== dateIndex);

      // Update preference order for remaining selected dates
      const reorderedSelectedDates = newSelectedDates.map((date, index) => ({
        ...date,
        preference: index,
        preferenceOrder: index,
      }));

      setInternalSelectedDates(reorderedSelectedDates);
      setInternalAvailableDates(
        [...internalAvailableDates, updatedDate].sort((a, b) => {
          const aDate = dayjs(a.DateOfInterview);
          const bDate = dayjs(b.DateOfInterview);
          return aDate.isBefore(bDate) ? -1 : aDate.isAfter(bDate) ? 1 : 0;
        }),
      );
    },
    [internalSelectedDates, internalAvailableDates],
  );

  // Handle drag end for reordering selected dates
  const handleDragEnd = useCallback((event) => {
    const { active, over } = event;

    if (!over) return;

    const activeId = active.id;
    const overId = over.id;

    if (activeId === overId) return;

    setInternalSelectedDates((items) => {
      const oldIndex = items.findIndex((item) => item.id === activeId);
      const newIndex = items.findIndex((item) => item.id === overId);

      if (oldIndex === -1 || newIndex === -1) return items;

      const newItems = arrayMove(items, oldIndex, newIndex);

      // Update preference order after reordering
      const reorderedItems = newItems.map((item, index) => ({
        ...item,
        preference: index,
        preferenceOrder: index,
      }));

      return reorderedItems;
    });
  }, []);

  // Add sync to Router function (called when confirming) - MUST be before early return
  const syncToRouter = useCallback(() => {
    setRouterSelectedDates(internalSelectedDates);
    // Return the current internal state for validation
    return {
      selectedDates: internalSelectedDates,
      selectedCount: internalSelectedDates.length,
    };
  }, [internalSelectedDates, setRouterSelectedDates]);

  // Expose sync function to parent component
  useEffect(() => {
    if (onSyncReady) {
      onSyncReady(syncToRouter);
    }
  }, [syncToRouter, onSyncReady]);

  if (!internalSelectedDates && !internalAvailableDates) {
    return <div>Loading...</div>;
  }

  return (
    <CandidateScheduleWrapper>
      {/* Unified List - Selected dates first (gray), then available dates (white) */}
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext
          items={internalSelectedDates.map((date) => date.id || date.pk_InterviewDate)}
          strategy={verticalListSortingStrategy}
        >
          {/* Selected Dates - Draggable and gray */}
          {internalSelectedDates.map((date, index) => (
            <SelectedDateItem
              key={date.id || date.pk_InterviewDate}
              date={date}
              index={index}
              isOrdinal={isOrdinal}
              singleDateChoice={singleDateChoice}
              onRemove={handleRemoveDate}
            />
          ))}
        </SortableContext>

        {/* Available Dates - Clickable and white */}
        {internalAvailableDates.map((date) => (
          <SelectableDateItem key={date.id || date.pk_InterviewDate} date={date} onClick={handleSelectDate} />
        ))}
      </DndContext>
    </CandidateScheduleWrapper>
  );
};

export default CandidateScheduleDateSelector;
